import { Locale } from 'date-fns'
import { enUS } from 'date-fns/locale/en-US'
import { useEffect, useRef, useState } from 'react'
import { useIntlContext } from '../../../components/IntlProviders.context'

function useRealRegisterLazyLoadedLocale() {
  const { intl } = useIntlContext()
  const [localeInfo, setLocaleInfo] = useState<Locale | null>(null)
  const isUnmountedRef = useRef(false)

  useEffect(() => {
    isUnmountedRef.current = false

    function cleanup() {
      isUnmountedRef.current = true
    }

    if (!intl.locale) {
      return cleanup
    }

    // Attempt to load the full locale first
    const localesToAttempt = [intl.locale]
    if (localeMap[intl.locale]) {
      // Then attempt to load the mapped locale
      localesToAttempt.push(localeMap[intl.locale])
    }
    const pieces = intl.locale.split('-')
    if (pieces.length > 2) {
      // Then attempt to load either the language-country or language-region
      localesToAttempt.push(`${pieces[0]}-${pieces[1]}`)
      localesToAttempt.push(`${pieces[0]}-${pieces[2]}`)
    }
    if (pieces.length > 1) {
      // Then attempt to load the base language last
      localesToAttempt.push(pieces[0])
    }

    Promise.all(localesToAttempt.map(loadLocaleFile)).then(modules => {
      // Filter out modules that didn't successfully load (returned as undefined)
      // Then the first module after that will be the best match
      const bestMatchModule = modules.filter(Boolean)[0] ?? enUS
      setLocaleInfo(bestMatchModule)
    })

    async function loadLocaleFile(locale: string): Promise<Locale | undefined> {
      try {
        // Relative `node_modules/.../${locale}.js` so Webpack can build a `require.context`-style
        // async chunk map (see `date-fns-locales/*-js` in webpack-stats). Vite cannot execute
        // those CJS `.js` files in the browser (`exports is not defined`); the showcase Vite
        // config rewrites this to `.mjs` for ESM. Do not switch the source to `.mjs`-only —
        // that would abandon the path Webpack already chunks for the real app.
        return findLocaleObjProp(
          await import(
            /* webpackChunkName: "date-fns-locales/[request]" */
            /* webpackExclude: /_lib/ */
            /* webpackMode: "lazy" */
            `../../../../../node_modules/date-fns/locale/${locale}.js`
          )
        )
      } catch {
        return undefined
      }
    }

    return cleanup
  }, [intl.locale])

  return { locale: intl.locale, localeInfo: !isUnmountedRef.current ? localeInfo : null }
}

// Some base locales don't have a corresponding file in date-fns, so we need to map them to the correct locale
export const localeMap: Record<string, string> = {
  en: 'en-US',
  fa: 'fa-IR',
  zh: 'zh-CN'
}

// if NODE_ENV === 'test' then we're running unit tests and the locale is already loaded globally
export const useRegisterLazyLoadedLocale = () => {
  const USE_MOCK_FOR_TEST = process.env.NODE_ENV === 'test' && process.env.ENABLE_LAZY_LOADING_LOCALE !== 'true'
  return USE_MOCK_FOR_TEST
    ? { locale: 'en-US', localeInfo: enUS, __mock: true }
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useRealRegisterLazyLoadedLocale()
}

function findLocaleObjProp(module: any) {
  // eslint-disable-next-line no-restricted-syntax
  for (const key in module) {
    if (Object.prototype.hasOwnProperty.call(module, key)) {
      const prop = module[key]
      // Assuming the locale object has specific methods like 'localize' and 'code' property
      if (prop && typeof prop === 'object' && typeof prop.code === 'string' && typeof prop.localize === 'object') {
        return prop
      }
    }
  }
  return null
}
